import { createHttpClient } from '@/lib/api/httpClient';

export type InboxNotificationCategory =
  | 'MARKETS'
  | 'FUNDS'
  | 'ACCOUNT'
  | 'COMMENTS'
  | 'MARKETING'
  | 'FUTURES'
  /** Scheduled-maintenance announcements. Always on — not listed in preferences. */
  | 'MAINTENANCE'
  /**
   * Rewards Hub: rewards ready, new rank, streak at risk, the week's rare card,
   * #1 on the leaderboard (notification-service v1.82; were MARKETING before).
   */
  | 'REWARDS_HUB';

/** `warning` / `error` are the futures-category spellings of `warn` / `failed`. */
export type InboxIconType =
  | 'success'
  | 'info'
  | 'warn'
  | 'alert'
  | 'failed'
  | 'neutral'
  | 'warning'
  | 'error';

export interface InboxNotification {
  id: string;
  type: string;
  category: InboxNotificationCategory;
  /** Max 256 chars, server-localized. */
  title: string;
  /** Max ~320 chars (v5), server-localized. Null = no body line. */
  body: string | null;
  /**
   * Gray italic sub-line shown between title and body on COMMENTS rows —
   * recipient's own parent-comment excerpt. Front-end prepends a localized
   * "Your comment:" prefix when rendering. Null for non-comment events.
   */
  quote: string | null;
  /** Semantic fallback when iconUrl is null. */
  iconType: InboxIconType | null;
  /**
   * Absolute CDN URL of an SVG icon. Null when type has no mapping —
   * render the row without an icon (or use iconType as semantic fallback).
   */
  iconUrl: string | null;
  ctaLabel: string | null;
  /** App-relative (`/wallet`) or absolute. Locale prefix stripped before passing to `Link`. */
  ctaUrl: string | null;
  read: boolean;
  createdAt: string;
  /**
   * Strapi internal id of the source comment (number/Long, NOT UUID) — only
   * present on COMMENTS-category rows. Used to power per-thread mute from the
   * row's 3-dot menu via `/mute/comments/{commentId}`.
   */
  commentId?: number | null;
  /**
   * v1.25 — user-visible entity type for COMMENTS rows. Pair with `sourceId`
   * to wire "Mute this market" / "Mute this news article" actions to
   * `POST /api/notifications/mute/sources/{sourceType}/{sourceId}`. Null for
   * non-comment rows (or older inbox rows pre-V18 migration).
   */
  sourceType?: 'MARKET' | 'NEWS_ARTICLE' | null;
  /** Pairs with `sourceType`. UUID for MARKET, BIGINT-as-string for NEWS_ARTICLE. */
  sourceId?: string | null;
}

export interface InboxPage {
  items: InboxNotification[];
  nextCursor: string | null;
  totalUnread: number;
}

export interface UnreadCount {
  count: number;
  byCategory: Partial<Record<InboxNotificationCategory, number>>;
}

export interface MarkAllReadResult {
  markedCount: number;
}

export interface InboxQuery {
  cursor?: string;
  limit?: number;
  category?: InboxNotificationCategory;
  unread?: boolean;
}

// Cookie-based inbox client — auth rides the HttpOnly cookie
const inboxHttp = createHttpClient({
  baseUrl: process.env.NEXT_PUBLIC_API_NOTIFICATION_URL,
});

export async function fetchInbox(params: InboxQuery = {}): Promise<InboxPage> {
  const res = await inboxHttp.get<InboxPage>('/notifications/inbox', {
    query: {
      cursor: params.cursor,
      limit: params.limit,
      category: params.category,
      unread: params.unread,
    },
  });
  return res ?? { items: [], nextCursor: null, totalUnread: 0 };
}

export async function fetchInboxUnreadCount(): Promise<UnreadCount> {
  const res = await inboxHttp.get<UnreadCount>('/notifications/inbox/unread-count');
  return res ?? { count: 0, byCategory: {} };
}

export async function markInboxNotificationRead(id: string): Promise<void> {
  await inboxHttp.post<void>(`/notifications/inbox/${id}/read`);
}

export async function markAllInboxNotificationsRead(
  category?: InboxNotificationCategory,
): Promise<MarkAllReadResult> {
  const res = await inboxHttp.post<MarkAllReadResult>('/notifications/inbox/mark-all-read', {
    query: category ? { category } : undefined,
  });
  return res ?? { markedCount: 0 };
}

export async function deleteInboxNotification(id: string): Promise<void> {
  await inboxHttp.delete<void>(`/notifications/inbox/${id}`);
}

export interface MutedComment {
  commentId: number;
  mutedAt: string;
}

export async function muteComment(commentId: number): Promise<void> {
  await inboxHttp.post<void>(`/notifications/mute/comments/${commentId}`);
}

export async function unmuteComment(commentId: number): Promise<void> {
  await inboxHttp.delete<void>(`/notifications/mute/comments/${commentId}`);
}

export async function fetchMutedComments(): Promise<MutedComment[]> {
  const res = await inboxHttp.get<MutedComment[]>('/notifications/mute/comments');
  return res ?? [];
}

export type MutedSourceType = 'MARKET' | 'NEWS_ARTICLE';

export interface MutedSource {
  sourceType: MutedSourceType;
  sourceId: string;
  mutedAt: string;
}

export async function muteSource(sourceType: MutedSourceType, sourceId: string): Promise<void> {
  await inboxHttp.post<void>(`/notifications/mute/sources/${sourceType}/${sourceId}`);
}

export async function unmuteSource(sourceType: MutedSourceType, sourceId: string): Promise<void> {
  await inboxHttp.delete<void>(`/notifications/mute/sources/${sourceType}/${sourceId}`);
}

export async function fetchMutedSources(): Promise<MutedSource[]> {
  const res = await inboxHttp.get<MutedSource[]>('/notifications/mute/sources');
  return res ?? [];
}
